import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { apiService, ExecutionHistorySummary } from '../services/apiService';
import { useProjects } from '../context/ProjectContext';
import { formatAppTimestamp, parseApiDateTime } from '../utils/dateTimeUtils';
import { mergeHistorySummariesByHistoryId } from '../utils/historyExecutionIdentity';
import { PROJECT_SELECT_CONTROL_WIDTH } from './Projects';
import HistoryExecutionDetailPanel from '../components/HistoryExecutionDetailPanel';

const SidebarHistory: React.FC = () => {
  const { selectedProject, projects, setSelectedProject, isLoading: projectsLoading } = useProjects();
  const currentProjectId = selectedProject?.id ?? null;
  const [history, setHistory] = useState<ExecutionHistorySummary[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Execution identity for Global History selection — never requestId. */
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  /** Replaces window.confirm so the browser does not show the origin in the title bar. */
  const [clearConfirmDialog, setClearConfirmDialog] = useState<null | 'delete-selected' | 'clear-all'>(null);
  const lastProjectIdRef = useRef<number | null>(null);
  const projectSelectRef = useRef<HTMLDivElement | null>(null);
  const [projectMenuWidth, setProjectMenuWidth] = useState(0);

  useEffect(() => {
    if (!currentProjectId) {
      setHistory([]);
      setHasMore(false);
      return;
    }
    const projectChanged = lastProjectIdRef.current !== currentProjectId;
    if (projectChanged) {
      lastProjectIdRef.current = currentProjectId;
      setPage(0);
      setSelectedHistoryId(null);
      try {
        localStorage.removeItem('globalHistoryDraft');
      } catch {
        // ignore
      }
    }
    const pageToFetch = projectChanged ? 0 : page;
    let isCancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.getExecutionHistoryPage(currentProjectId, pageToFetch, 10);
        if (!isCancelled) {
          setHistory(prev => {
            const list = res.content || [];
            if (pageToFetch === 0) return list;
            // Merge by historyId only — keep repeated executions of the same requestId.
            return mergeHistorySummariesByHistoryId(prev, list);
          });
          setHasMore(!res.last);
        }
      } catch {
        if (!isCancelled) {
          setError('Failed to load history');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };
    fetchHistory();
    return () => {
      isCancelled = true;
    };
  }, [currentProjectId, page, refreshKey]);

  useEffect(() => {
    const handleHistoryUpdate = () => {
      setHistory([]);
      setHasMore(true);
      setPage(0);
      setRefreshKey(prev => prev + 1);
    };
    window.addEventListener('global-history-updated', handleHistoryUpdate as EventListener);
    return () => {
      window.removeEventListener('global-history-updated', handleHistoryUpdate as EventListener);
    };
  }, []);

  const handleSelect = (item: ExecutionHistorySummary) => {
    // Select by historyId immediately; detail panel loads GET /history/{historyId}.
    // Do not remount Collections — viewing History is not opening the live editor.
    setSelectedHistoryId(item.id);
  };

  const methodColor = (method?: string) => {
    // Use a single consistent color for all methods in history
    return '#ef6c00';
  };

  const groupLabel = (date: Date) => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const diffDays = Math.floor((startOfToday - startOfDate) / (24 * 60 * 60 * 1000));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return history;

    // Allow searching by method, URL, request/collection names, and created-at date/time.
    return history.filter(item => {
      const created = parseApiDateTime(item.createdAt);
      const createdDate = created
        ? created.toISOString().slice(0, 10) // YYYY-MM-DD
        : '';
      const createdLocal = formatAppTimestamp(item.createdAt, '').toLowerCase();

      const haystack = [
        item.method,
        item.url,
        item.requestName,
        item.collectionName,
        createdDate,
        createdLocal,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [history, search]);

  const grouped = useMemo(() => {
    const groups: { label: string; items: ExecutionHistorySummary[] }[] = [];
    filtered.forEach(item => {
      const date = parseApiDateTime(item.createdAt) ?? new Date();
      const label = groupLabel(date);
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
    });
    return groups;
  }, [filtered]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 'calc(100vh - 100px)',
        flex: 1,
        minWidth: 0,
        gap: 1,
        pt: 1.3,
      }}
    >
      {/* Header matching Environments layout */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          mb: 3,
          flexShrink: 0,
          overflow: 'visible',
          pt: 1.5,
        }}
      >
        <Typography
          variant="h4"
          sx={{ ml: 1.5, pb: 0.5, fontSize: '1.5rem !important', fontWeight: 700, color: '#ef6c00' }}
        >
          History
        </Typography>
        <FormControl
          ref={projectSelectRef}
          size="small"
          variant="outlined"
          sx={{
            width: PROJECT_SELECT_CONTROL_WIDTH,
            flexShrink: 0,
            overflow: 'visible',
            '& .MuiInputLabel-root': {
              backgroundColor: '#ffffff',
              px: 0.5,
              lineHeight: 1.25,
              zIndex: 1,
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
              backgroundColor: '#ffffff',
              paddingLeft: '4px',
              paddingRight: '4px',
            },
            '& .MuiOutlinedInput-root': {
              minHeight: 40,
            },
            '& .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '100%',
            },
            '& .MuiOutlinedInput-notchedOutline legend span': {
              paddingLeft: '4px',
              paddingRight: '4px',
            },
          }}
        >
          <InputLabel id="project-select-label" shrink>
            Project
          </InputLabel>
          <Select
            labelId="project-select-label"
            id="project-select"
            value={selectedProject ? selectedProject.id : ''}
            label="Project"
            onChange={(e) => {
              const projectId = e.target.value as number;
              const project = projects.find(p => p.id === projectId);
              setSelectedProject(project || null);
            }}
            onOpen={() => {
              const width = projectSelectRef.current?.getBoundingClientRect().width ?? 0;
              if (width > 0) setProjectMenuWidth(Math.round(width));
            }}
            sx={{
              width: '100%',
              bgcolor: '#ffffff',
              '& .MuiSelect-select': {
                fontSize: '0.8125rem',
                fontWeight: 500,
                lineHeight: 1.35,
                display: 'block',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
              },
            }}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: {
                  maxHeight: 40 * 8 + 16,
                  mt: 0.5,
                  boxSizing: 'border-box',
                  ...(projectMenuWidth > 0
                    ? {
                        width: `${projectMenuWidth}px !important`,
                        minWidth: `${projectMenuWidth}px !important`,
                        maxWidth: `${projectMenuWidth}px !important`,
                      }
                    : {}),
                },
              },
              MenuListProps: {
                sx: {
                  maxHeight: 40 * 8 + 16,
                  overflowY: 'auto',
                  py: 0.5,
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                  '& .MuiMenuItem-root': {
                    minHeight: 40,
                    whiteSpace: 'nowrap',
                  },
                },
              },
            }}
          >
            {projectsLoading ? (
              <MenuItem disabled value="">
                <CircularProgress size={20} /> Loading...
              </MenuItem>
            ) : (
              projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))
            )}
            {projects.length === 0 && !projectsLoading && (
              <MenuItem disabled>No projects found</MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Left panel: global history list, styled like Environments sidebar */}
        <Paper
          elevation={0}
          sx={{
            width: 280,
            minWidth: 280,
            height: '100%',
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
        <Box sx={{ px: 2, pt: 2, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search history"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={!currentProjectId || loading || history.length === 0}
              onClick={() => {
                if (checkedIds.length > 0) {
                  setClearConfirmDialog('delete-selected');
                } else {
                  setClearConfirmDialog('clear-all');
                }
              }}
                >
                  Clear
                </Button>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            pt: 1,
            WebkitOverflowScrolling: 'touch',
          }}
        >

          {loading && history.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : !currentProjectId ? (
            <Typography color="text.secondary">Select a project to view history.</Typography>
          ) : history.length === 0 ? (
            <Typography color="text.secondary">No History is Available</Typography>
          ) : filtered.length === 0 ? (
            <Typography color="text.secondary">No history found.</Typography>
          ) : (
            <Box>
              {grouped.map(group => (
                <Box key={group.label} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                    {group.label}
                  </Typography>
                  <List dense sx={{ pt: 0 }}>
                    {group.items.map((row) => (
                      <ListItemButton
                        key={row.id}
                        onClick={() => handleSelect(row)}
                        selected={selectedHistoryId === row.id}
                        sx={{ borderRadius: 1, pr: 0.5 }}
                        disableRipple={false}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={checkedIds.includes(row.id)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCheckedIds(prev =>
                                    prev.includes(row.id)
                                      ? prev.filter(id => id !== row.id)
                                      : [...prev, row.id]
                                  );
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: methodColor(row.method), fontWeight: 600, minWidth: 44 }}
                              >
                                {row.method?.toUpperCase() || 'REQ'}
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                {row.requestName || 'Untitled Request'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            `${row.collectionName || 'Unknown Collection'} • ${formatAppTimestamp(row.createdAt, '—')}`
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pb: 1 }}>
            {hasMore && (
              <Button onClick={() => setPage(prev => prev + 1)} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            )}
          </Box>
        </Box>
        </Paper>

        {/* Right panel: saved execution snapshot (not live Collections editor) */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%' }}>
          {selectedHistoryId ? (
            <HistoryExecutionDetailPanel key={selectedHistoryId} historyId={selectedHistoryId} />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">
                Select a history entry to view the saved execution.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={clearConfirmDialog !== null}
        onClose={() => setClearConfirmDialog(null)}
        aria-labelledby="history-clear-dialog-title"
      >
        <DialogTitle id="history-clear-dialog-title">
          {clearConfirmDialog === 'delete-selected' ? 'Delete selected history' : 'Clear all history'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {clearConfirmDialog === 'delete-selected'
              ? 'Delete the selected history items? This cannot be undone.'
              : 'Clear all history for this project? This cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmDialog(null)} color="inherit">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              const mode = clearConfirmDialog;
              setClearConfirmDialog(null);
              if (mode === 'delete-selected') {
                try {
                  await Promise.all(
                    checkedIds.map(id => apiService.deleteExecutionHistoryItem(id))
                  );
                  setHistory(prev => prev.filter(item => !checkedIds.includes(item.id)));
                  if (checkedIds.includes(selectedHistoryId ?? -1)) {
                    setSelectedHistoryId(null);
                    try {
                      localStorage.removeItem('globalHistoryDraft');
                    } catch {
                      // ignore
                    }
                  }
                  setCheckedIds([]);
                  if (history.length <= checkedIds.length) {
                    setHasMore(false);
                  }
                } catch {
                  // ignore errors for now
                }
                return;
              }
              if (mode === 'clear-all') {
                await apiService.clearExecutionHistory(currentProjectId ?? undefined);
                setHistory([]);
                setPage(0);
                setHasMore(false);
                setSelectedHistoryId(null);
                setCheckedIds([]);
                try {
                  localStorage.removeItem('globalHistoryDraft');
                } catch {
                  // ignore
                }
              }
            }}
            autoFocus
          >
            {clearConfirmDialog === 'delete-selected' ? 'Delete' : 'Clear all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SidebarHistory;
